import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useMatch } from "react-router-dom";
import { setActiveQuestion } from "../../../redux/actions/chatActions";
import { AppDispatch } from "../../../redux/store";
import { useAppDispatch, useAppSelector } from "../../../hooks/app";
import { fetchChatQuestionHistory } from "../../../redux/reducers/chatQuestionHistory/chatQuestionHistoryReducer";
import ChatQuestion from "./ChatQuestion/ChatQuestion";
import { Button, LoadingSpinner } from "pepsico-ds";
import { useChatNavigate } from "../../../hooks/app/chatNavigate";

const PAGE_SIZE = 10;

interface ChatQuestionHistoryProps {
  hideTitle?: boolean;
}

const ChatQuestionHistory: React.FC<ChatQuestionHistoryProps> = ({
  hideTitle = false,
}) => {
  const { gotoChat } = useChatNavigate();
  const appDispatch = useAppDispatch();
  const [chatPage, setChatPage] = useState(1);
  const { isLoading, chatSession, isEndOfData, isError } = useAppSelector(
    (state) => state.chatQuestionHistory
  );
  const { activeQuestionId } = useAppSelector((state) => state.chat);
  const dispatch = useDispatch<AppDispatch>();

  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [showFetchMoreButton, setShowFetchMoreButton] = useState(false);
  const chatListRef = useRef<HTMLDivElement | null>(null);

  const chatPath = useMatch("/spend-wise-hub/chat-assistant/:sessionId");
  const newChatPath = useMatch("/spend-wise-hub/chat-assistant");

  const handleChatSelection = (chatId: string, questionId: string) => {
    dispatch(setActiveQuestion(chatId, questionId)); // Set the active question
    gotoChat({ sessionId: chatId, questionId }); // Navigate to the chat
  };

  useEffect(() => {
    appDispatch(fetchChatQuestionHistory({ page: 1, limit: PAGE_SIZE }));
    setChatPage(1);
  }, [appDispatch]);

  const fetchMore = useCallback(() => {
    if (isFetchingMore || isLoading || isEndOfData) return;
    setIsFetchingMore(true);
    appDispatch(
      fetchChatQuestionHistory({ page: chatPage + 1, limit: PAGE_SIZE })
    )
      .then(() => setChatPage(chatPage + 1))
      .catch((error) => console.log(error))
      .finally(() => setIsFetchingMore(false));
  }, [appDispatch, chatPage, isFetchingMore, isLoading, isEndOfData]);

  useEffect(() => {
    const chatList = chatListRef.current;
    if (!chatList) return;

    const handleScroll = () => {
      const reachedBottom =
        chatList.scrollTop + chatList.clientHeight >=
        chatList.scrollHeight - 10;
      if (reachedBottom && !isEndOfData) {
        fetchMore();
      }
    };

    chatList.addEventListener("scroll", handleScroll);

    // Check if the scrollbar is not present
    if (
      chatSession.length > 0 &&
      chatList.scrollHeight <= chatList.clientHeight &&
      !isEndOfData
    ) {
      setShowFetchMoreButton(true);
    } else {
      setShowFetchMoreButton(false);
    }

    return () => chatList.removeEventListener("scroll", handleScroll);
  }, [isEndOfData, fetchMore]);

  const getisActive = (sessionId: string) => {
    return (
      chatPath &&
      !newChatPath?.pattern?.path &&
      chatPath.params.sessionId === sessionId
    );
  };

  return (
    <>
      {/* Header Section - Only show if hideTitle is false */}
      {!hideTitle && (
        <div
          className="$font-heading-h6-bold"
          style={{ marginLeft: "10px", marginBottom: "10px" }}
        >
          Chat History
        </div>
      )}
      {isLoading === false
        ? isError && (
            <div
              className="$font-body-medium $color-feedback-error"
              style={{ marginTop: "2px", textAlign: "center" }}
            >
              Failed to fetch chat history
            </div>
          )
        : null}

      {/* List of Chat Items */}
      <div
        style={{
          flexGrow: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          height: hideTitle ? "calc(100vh - 240px)" : "calc(100vh - 200px)",
        }}
        ref={chatListRef}
      >
        {chatSession.length === 0 && !isLoading ? (
          <div
            className="$font-body-medium"
            style={{ marginTop: "20px", textAlign: "center" }}
          >
            No chat history
          </div>
        ) : (
          <div style={{ width: "100%" }}>
            {chatSession.map((session) => (
              <div key={session.chat_id}>
                {session.chathistories.map((chat, i) => (
                  <ChatQuestion
                    key={chat.question_id}
                    sessionId={session.chat_id}
                    session_name={chat.question}
                    questionId={chat.question_id}
                    activeQuestionId={activeQuestionId}
                    index={i}
                    onChatClick={() =>
                      handleChatSelection(chat.chat_id, chat.question_id)
                    }
                  />
                ))}
                <div style={{ margin: "0 8%", border: "1px solid #eee" }}></div>
              </div>
            ))}
          </div>
        )}
        {showFetchMoreButton && !isEndOfData && (
          <Button
            variant="tertiary"
            style={{
              marginTop: "20px",
              alignSelf: "center",
              width: "235px",
              justifyContent: "center",
            }}
            onClick={fetchMore}
          >
            Load More
          </Button>
        )}
      </div>

      {/* Lazy Loading Spinner */}
      {isFetchingMore && (
        <div
          style={{
            marginTop: "16px",
            justifyContent: "center",
            display: "flex",
          }}
        >
          <LoadingSpinner
            size={{
              height: "30px",
              width: "30px",
            }}
          />
        </div>
      )}
    </>
  );
};

export default ChatQuestionHistory;
